import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2, BarChart3, User } from "lucide-react";

// LocalStorage helpers
function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function save<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

const LS_KEYS = {
  obs: "gelds2_observations",
  custom: "gelds2_customIndicators",
  instr: "gelds2_instructionNotes",
  needs: "gelds2_needsNotes",
};


export default function GELDSTracker() {
  const [view, setView] = useState<"tracker" | "profiles">("tracker");
  const [expandedStandard, setExpandedStandard] = useState<string | null>(null);
  const [observations, setObservations] = useState<Record<string, any>>(load(LS_KEYS.obs, {}));
  const [customIndicators, setCustomIndicators] = useState<Record<string, any[]>>(load(LS_KEYS.custom, {}));
  const [showNewIndicatorForm, setShowNewIndicatorForm] = useState<string | null>(null);
  const [newIndicatorText, setNewIndicatorText] = useState("");
  const [newIndicatorWS, setNewIndicatorWS] = useState("");
  const [instructionNotes, setInstructionNotes] = useState<Record<string, string>>(load(LS_KEYS.instr, {}));
  const [needsNotes, setNeedsNotes] = useState<Record<string, string>>(load(LS_KEYS.needs, {}));
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => { save(LS_KEYS.obs, observations); }, [observations]);
  useEffect(() => { save(LS_KEYS.custom, customIndicators); }, [customIndicators]);
  useEffect(() => { save(LS_KEYS.instr, instructionNotes); }, [instructionNotes]);
  useEffect(() => { save(LS_KEYS.needs, needsNotes); }, [needsNotes]);

  // Export all tracker data as JSON
  const handleExport = () => {
    const data = {
      observations,
      customIndicators,
      instructionNotes,
      needsNotes,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gelds-tracker-backup-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  // Import tracker data from JSON
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (typeof data === "object" && data) {
          setObservations(data.observations || {});
          setCustomIndicators(data.customIndicators || {});
          setInstructionNotes(data.instructionNotes || {});
          setNeedsNotes(data.needsNotes || {});
          alert("Data imported successfully!");
        } else {
          alert("Invalid file format.");
        }
      } catch {
        alert("Failed to import data. Make sure the file is a valid backup.");
      }
    };
    reader.readAsText(file);
    // Reset input so same file can be imported again if needed
    e.target.value = "";
  };

  // Clear all tracker data after confirmation
  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear ALL tracker data? This cannot be undone. Make sure you have exported a backup first!")) {
      setObservations({});
      setCustomIndicators({});
      setInstructionNotes({});
      setNeedsNotes({});
      // Also clear from localStorage immediately
      save(LS_KEYS.obs, {});
      save(LS_KEYS.custom, {});
      save(LS_KEYS.instr, {});
      save(LS_KEYS.needs, {});
    }
  };

  const standards = {
    "Personal and Social Development": {
      indicators: [
        { id: "PSD-A1", text: "Demonstrates self-confidence", workSampling: "Self-Concept" },
        { id: "PSD-A2", text: "Shows some self-direction", workSampling: "Self-Concept" },
        { id: "PSD-B1", text: "Follows simple classroom rules and routines", workSampling: "Self-Control" },
        { id: "PSD-B2", text: "Manages transitions", workSampling: "Self-Control" },
        { id: "PSD-C1", text: "Shows eagerness and curiosity as a learner", workSampling: "Approaches to Learning" },
        { id: "PSD-C2", text: "Attends to tasks and seeks help when encountering a problem", workSampling: "Approaches to Learning" },
        { id: "PSD-C3", text: "Approaches tasks with flexibility and inventiveness", workSampling: "Approaches to Learning" },
        { id: "PSD-D1", text: "Interacts easily with one or more children", workSampling: "Interaction with Others" },
        { id: "PSD-D2", text: "Interacts easily with familiar adults", workSampling: "Interaction with Others" },
        { id: "PSD-D3", text: "Participates in the group life of the class", workSampling: "Interaction with Others" },
        { id: "PSD-D4", text: "Identifies some feelings and responds to those of others", workSampling: "Interaction with Others" },
        { id: "PSD-D5", text: "Begins to use simple strategies to resolve conflict", workSampling: "Interaction with Others" },
      ],
    },
    "Language and Literacy": {
      indicators: [
        { id: "LL-A1", text: "Gains meaning by listening", workSampling: "Listening" },
        { id: "LL-A2", text: "Follows two- or three-step directions", workSampling: "Listening" },
        { id: "LL-B1", text: "Speaks clearly enough to be understood without contextual clues", workSampling: "Speaking" },
        { id: "LL-B2", text: "Follows rules for conversation", workSampling: "Speaking" },
        { id: "LL-B3", text: "Uses expanded vocabulary and language for a variety of purposes", workSampling: "Speaking" },
        { id: "LL-C1", text: "Begins to develop knowledge of letters", workSampling: "Reading" },
        { id: "LL-C2", text: "Demonstrates phonological awareness", workSampling: "Reading" },
        { id: "LL-C3", text: "Shows appreciation and understanding of books and reading", workSampling: "Reading" },
        { id: "LL-C4", text: "Recounts some key ideas and details from text", workSampling: "Reading" },
        { id: "LL-D1", text: "Represents ideas and stories through pictures, dictation, and play", workSampling: "Writing" },
        { id: "LL-D2", text: "Uses letter-like shapes, symbols, and letters to convey meaning", workSampling: "Writing" },
        { id: "LL-D3", text: "Understands purposes for writing", workSampling: "Writing" },
      ],
    },
    "Mathematical Thinking": {
      indicators: [
        { id: "MT-A1", text: "Begins to make sense of problems and uses simple strategies to solve them", workSampling: "Processes and Practices" },
        { id: "MT-A2", text: "Reasons quantitatively and begins to use some tools", workSampling: "Processes and Practices" },
        { id: "MT-A3", text: "Uses words and representations to describe mathematical ideas", workSampling: "Processes and Practices" },
        { id: "MT-A4", text: "Begins to recognize patterns and makes simple generalizations", workSampling: "Processes and Practices" },
        { id: "MT-B1", text: "Counts with understanding", workSampling: "Number" },
        { id: "MT-B2", text: "Shows beginning understanding of number and quantity", workSampling: "Number" },
        { id: "MT-C1", text: "Understands and begins to apply addition and subtraction to problems", workSampling: "Operations and Algebraic Thinking" },
        { id: "MT-D1", text: "Orders, compares, and describes objects according to a single attribute", workSampling: "Measurement" },
        { id: "MT-D2", text: "Participates in measuring activities", workSampling: "Measurement" },
        { id: "MT-F1", text: "Shows understanding of and uses several positional words", workSampling: "Geometry" },
        { id: "MT-F2", text: "Begins to recognize and describe the attributes of shapes", workSampling: "Geometry" },
        { id: "MT-F3", text: "Composes and decomposes shapes", workSampling: "Geometry" },
      ],
    },
    "Physical Development": {
      indicators: [
        { id: "PD-B2", text: "Uses eye-hand coordination to perform tasks", workSampling: "Physical Development" },
        { id: "PD-B3", text: "Shows beginning control of writing, drawing, and art tools", workSampling: "Physical Development" },
        { id: "PD-C1", text: "Performs some self-care tasks independently", workSampling: "Self-Care, Health, and Safety" },
        { id: "PD-C2", text: "Follows basic safety rules with reminders", workSampling: "Self-Care, Health, and Safety" },
      ],
    },
    "Science": {
      indicators: [
        { id: "S1", text: "Observes and explores objects", workSampling: "Scientific Inquiry" },
        { id: "S2", text: "Shows curiosity about living things", workSampling: "Life Science" },
        { id: "S3", text: "Investigates physical properties", workSampling: "Physical Science" },
      ],
    },
    "Social Studies": {
      indicators: [
        { id: "SS1", text: "Recognizes community helpers", workSampling: "Community & Roles" },
        { id: "SS2", text: "Shows awareness of family and culture", workSampling: "Cultural Awareness" },
        { id: "SS3", text: "Demonstrates environmental awareness", workSampling: "Geography & Environment" },
      ],
    },
    "Arts": {
      indicators: [
        { id: "A1", text: "Engages in creative expression through art", workSampling: "Visual Arts" },
        { id: "A2", text: "Participates in music and movement", workSampling: "Music & Movement" },
        { id: "A3", text: "Engages in dramatic play", workSampling: "Dramatic Play" },
      ],
    },
  };

  const addObservation = (indicatorId: string, childName: string, date: string, notes: string) => {
    const key = `${indicatorId}-${Date.now()}`;
    setObservations((prev) => ({ ...prev, [key]: { indicatorId, childName, date, notes } }));
  };

  const deleteObservation = (key: string) => {
    setObservations((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const addCustomIndicator = (category: string) => {
    if (newIndicatorText && newIndicatorWS) {
      const id = `custom-${Date.now()}`;
      setCustomIndicators((prev) => ({
        ...prev,
        [category]: [...(prev[category] || []), { id, text: newIndicatorText, workSampling: newIndicatorWS }],
      }));
      setNewIndicatorText("");
      setNewIndicatorWS("");
      setShowNewIndicatorForm(null);
    }
  };

  const deleteCustomIndicator = (category: string, id: string) => {
    setCustomIndicators((prev) => ({
      ...prev,
      [category]: (prev[category] || []).filter((ind) => ind.id !== id),
    }));
  };

  const getObservationsForIndicator = (indicatorId: string) =>
    Object.entries(observations).filter(([_, obs]) => obs.indicatorId === indicatorId);

  const getAllChildren = () => {
    const children = new Set<string>();
    Object.values(observations).forEach((obs: any) => children.add(obs.childName));
    return Array.from(children).sort();
  };

  const getChildProfile = (childName: string) => {
    const profile: Record<string, any> = {};
    Object.entries(standards).forEach(([category, data]) => {
      profile[category] = {
        indicators: data.indicators.map((ind) => ({
          ...ind,
          observations: Object.entries(observations).filter(
            ([_, obs]) => obs.indicatorId === ind.id && obs.childName === childName
          ),
          hasInstruction: instructionNotes[`${childName}-${ind.id}`] || "",
          hasNeeds: needsNotes[`${childName}-${ind.id}`] || "",
        })),
        custom: (customIndicators[category] || []).map((ind) => ({
          ...ind,
          observations: Object.entries(observations).filter(
            ([_, obs]) => obs.indicatorId === ind.id && obs.childName === childName
          ),
          hasInstruction: instructionNotes[`${childName}-${ind.id}`] || "",
          hasNeeds: needsNotes[`${childName}-${ind.id}`] || "",
        })),
      };
    });
    return profile;
  };

  return (
    <div className="container">
      <h1>GELDS & Work Sampling Tracker</h1>
      <div style={{ marginBottom: "1em" }}>
        <button onClick={() => setView("tracker")} style={{ marginRight: "0.5em" }}>
          <Plus size={16} /> Tracker
        </button>
        <button onClick={() => setView("profiles")}> <BarChart3 size={16} /> Student Profiles </button>
      </div>
      {/* Export/Import/Clear UI */}
      {view === "tracker" && (
        <div style={{ marginBottom: "1.5em", display: "flex", gap: "0.5em", flexWrap: "wrap" }}>
          <button onClick={handleExport} style={{ background: "#0ea5e9" }}>
            Export Backup
          </button>
          <button onClick={() => fileInputRef.current?.click()} style={{ background: "#f59e42" }}>
            Import Backup
          </button>
          <input
            type="file"
            accept="application/json"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleImport}
          />
          <button onClick={handleClearAll} style={{ background: "#ef4444" }}>
            Clear All Data
          </button>
        </div>
      )}
      {view === "tracker" ? (
        <div>
          {Object.entries(standards).map(([category, data]) => (
            <div key={category} className="section">
              <button
                onClick={() => setExpandedStandard(expandedStandard === category ? null : category)}
                style={{ fontWeight: "bold", marginBottom: "0.5em" }}
              >
                {category} {expandedStandard === category ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedStandard === category && (
                <div>
                  {[...data.indicators, ...(customIndicators[category] || [])].map((indicator) => (
                    <div key={indicator.id} className="indicator">
                      <div style={{ marginBottom: "0.3em" }}>
                        <span style={{ fontWeight: "500" }}>{indicator.text}</span>
                        <span style={{ fontSize: "0.9em", color: "#555", marginLeft: "0.5em" }}>
                          ({indicator.workSampling})
                        </span>
                      </div>
                      <div>
                        {getObservationsForIndicator(indicator.id).map(([key, obs]) => (
                          <div key={key} className="observation">
                            <span style={{ fontWeight: "500" }}>{obs.childName}</span>: {obs.date} — {obs.notes}
                            <button
                              style={{ float: "right", background: "#ef4444", color: "#fff", border: "none", padding: "0.2em 0.5em", borderRadius: "3px" }}
                              onClick={() => deleteObservation(key)}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <AddObservationForm indicatorId={indicator.id} onAdd={addObservation} />
                      {indicator.id.startsWith("custom-") && (
                        <button
                          style={{ background: "#ef4444", color: "#fff", border: "none", padding: "0.2em 0.5em", borderRadius: "3px", marginTop: "0.3em" }}
                          onClick={() => deleteCustomIndicator(category, indicator.id)}
                        >
                          <Trash2 size={12} /> Delete Indicator
                        </button>
                      )}
                    </div>
                  ))}
                  {showNewIndicatorForm === category ? (
                    <div style={{ background: "#f1f5f9", padding: "0.7em", borderRadius: "4px", marginTop: "0.5em" }}>
                      <input
                        type="text"
                        placeholder="Indicator description"
                        value={newIndicatorText}
                        onChange={(e) => setNewIndicatorText(e.target.value)}
                        style={{ width: "100%", marginBottom: "0.3em" }}
                      />
                      <input
                        type="text"
                        placeholder="Work Sampling alignment"
                        value={newIndicatorWS}
                        onChange={(e) => setNewIndicatorWS(e.target.value)}
                        style={{ width: "100%", marginBottom: "0.3em" }}
                      />
                      <button onClick={() => addCustomIndicator(category)} style={{ marginRight: "0.5em" }}>
                        Add
                      </button>
                      <button onClick={() => { setShowNewIndicatorForm(null); setNewIndicatorText(""); setNewIndicatorWS(""); }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      style={{ background: "#22c55e", color: "#fff", border: "none", padding: "0.2em 0.5em", borderRadius: "3px", marginTop: "0.3em" }}
                      onClick={() => setShowNewIndicatorForm(category)}
                    >
                      <Plus size={12} /> Add Indicator
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <ProfileView
          standards={standards}
          observations={observations}
          customIndicators={customIndicators}
          instructionNotes={instructionNotes}
          needsNotes={needsNotes}
          setInstructionNotes={setInstructionNotes}
          setNeedsNotes={setNeedsNotes}
        />
      )}
    </div>
  );
}

function AddObservationForm({ indicatorId, onAdd }: { indicatorId: string; onAdd: (indicatorId: string, childName: string, date: string, notes: string) => void }) {
  const [showForm, setShowForm] = useState(false);
  const [childName, setChildName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (childName && notes) {
      onAdd(indicatorId, childName, date, notes);
      setChildName("");
      setDate(new Date().toISOString().split("T")[0]);
      setNotes("");
      setShowForm(false);
    }
  };

  return !showForm ? (
    <button style={{ background: "#3b82f6", color: "#fff", border: "none", padding: "0.2em 0.5em", borderRadius: "3px", marginTop: "0.3em" }} onClick={() => setShowForm(true)}>
      <Plus size={12} /> Add Observation
    </button>
  ) : (
    <div style={{ background: "#e0e7ef", padding: "0.7em", borderRadius: "4px", marginTop: "0.5em" }}>
      <input
        type="text"
        placeholder="Child's name"
        value={childName}
        onChange={(e) => setChildName(e.target.value)}
        style={{ width: "100%", marginBottom: "0.3em" }}
      />
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        style={{ width: "100%", marginBottom: "0.3em" }}
      />
      <textarea
        placeholder="Observation notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        style={{ width: "100%", marginBottom: "0.3em" }}
        rows={2}
      />
      <button onClick={handleSubmit} style={{ marginRight: "0.5em" }}>
        Save
      </button>
      <button onClick={() => setShowForm(false)}>
        Cancel
      </button>
    </div>
  );
}

function ProfileView({ standards, observations, customIndicators, instructionNotes, needsNotes, setInstructionNotes, setNeedsNotes }: any) {
  const children = (() => {
    const set = new Set<string>();
    Object.values(observations).forEach((obs: any) => set.add(obs.childName));
    return Array.from(set).sort();
  })();
  const [selectedChild, setSelectedChild] = useState(children[0] || "");
  const profileRef = React.useRef<HTMLDivElement>(null);

  // Print only the profile section
  const handlePrint = () => {
    if (!profileRef.current) return;
    // Create a new window with just the profile content
    const printContents = profileRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=800,width=800');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Student Profile</title>');
      printWindow.document.write('<style>body{font-family:sans-serif;} .print-profile{max-width:700px;margin:auto;} h2,h3{margin-top:1em;} textarea{display:none;} @media print { button { display: none !important; } }</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write('<div class="print-profile">' + printContents + '</div>');
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);
    }
  };

  if (!selectedChild) return <div>No student data yet. Add observations in the Tracker view first.</div>;

  const profile = (() => {
    const p: Record<string, any> = {};
    Object.entries(standards).forEach(([category, data]) => {
      const d = data as { indicators: any[] };
      p[category] = {
        indicators: d.indicators.map((ind: any) => ({
          ...ind,
          observations: Object.entries(observations).filter(
            ([_, obs]) => {
              const o = obs as any;
              return o.indicatorId === ind.id && o.childName === selectedChild;
            }
          ),
          hasInstruction: instructionNotes[`${selectedChild}-${ind.id}`] || "",
          hasNeeds: needsNotes[`${selectedChild}-${ind.id}`] || "",
        })),
        custom: (customIndicators[category] || []).map((ind: any) => ({
          ...ind,
          observations: Object.entries(observations).filter(
            ([_, obs]) => {
              const o = obs as any;
              return o.indicatorId === ind.id && o.childName === selectedChild;
            }
          ),
          hasInstruction: instructionNotes[`${selectedChild}-${ind.id}`] || "",
          hasNeeds: needsNotes[`${selectedChild}-${ind.id}`] || "",
        })),
      };
    });
    return p;
  })();

  return (
    <div>
      <h2>Student Profile</h2>
      <label>
        Select Student:
        <select value={selectedChild} onChange={e => setSelectedChild(e.target.value)}>
          {children.map(child => (
            <option key={child} value={child}>{child}</option>
          ))}
        </select>
      </label>
      <button onClick={handlePrint} style={{ margin: '1em 0', background: '#0ea5e9', color: '#fff', border: 'none', padding: '0.5em 1em', borderRadius: '4px' }}>
        Print Profile
      </button>
      <div ref={profileRef}>
        {Object.entries(profile).map(([category, data]) => (
          <div key={category} style={{ marginTop: "1em", background: "#f1f5f9", borderRadius: "4px", padding: "1em" }}>
            <h3>{category}</h3>
            {[...data.indicators, ...data.custom].map((indicator: any) => (
              <div key={indicator.id} style={{ marginBottom: "0.7em" }}>
                <div style={{ fontWeight: "500" }}>{indicator.text}</div>
                <div style={{ fontSize: "0.9em", color: "#555" }}>({indicator.workSampling})</div>
                <div>
                  {indicator.observations.length > 0 ? (
                    <div>
                      <span style={{ color: "#16a34a" }}>{indicator.observations.length} observation(s) documented</span>
                      {indicator.observations.map(([_, obs]: any) => (
                        <div key={_} style={{ fontSize: "0.95em", color: "#444" }}>{obs.date}: {obs.notes}</div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: "#888" }}>No observations yet</span>
                  )}
                  <textarea
                    placeholder="Add notes about instruction provided..."
                    value={instructionNotes[`${selectedChild}-${indicator.id}`] || ""}
                    onChange={e => setInstructionNotes((prev: any) => ({ ...prev, [`${selectedChild}-${indicator.id}`]: e.target.value }))}
                    style={{ width: "100%", marginTop: "0.3em" }}
                    rows={2}
                  />
                  <textarea
                    placeholder="Document instructional needs and next steps..."
                    value={needsNotes[`${selectedChild}-${indicator.id}`] || ""}
                    onChange={e => setNeedsNotes((prev: any) => ({ ...prev, [`${selectedChild}-${indicator.id}`]: e.target.value }))}
                    style={{ width: "100%", marginTop: "0.3em" }}
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
